/**
 * NotificationCenter Component
 *
 * Bell icon with badge showing unread count, and dropdown with notifications list.
 * For VENDOR and VENDOR_STAFF roles, also shows low-stock addon alerts below general notifications.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { Role, formatISTLocale } from '@lov/shared';
import {
  Box,
  IconButton,
  Badge,
  Popover,
  Typography,
  Button,
  Divider,
  Skeleton,
  List,
  ListItem,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  DoneAll as MarkAllReadIcon,
  Inbox as EmptyIcon,
  Close as CloseIcon,
  Inventory2Outlined as StockIcon,
} from '@mui/icons-material';
import { NotificationItem } from './NotificationItem';
import {
  useNotifications,
  useUnreadCount,
  useMarkAsRead,
  useMarkAllAsRead,
  useDeleteNotification,
  type Notification,
} from '../hooks/useNotifications';
import { getNotificationRoute } from '../utils/getNotificationRoute';
import { validatedApi } from '../../../lib/api/validated-client';
import { useContext } from 'react';
import { VenueContext } from '../../../contexts/VenueContext';

// ─── Low-stock alerts (vendor only) ────────────────────────────────────────

interface LowStockAlert {
  addonName: string;
  venueName: string;
  date: string;
  remaining: number;
  totalCapacity: number;
  severity: 'CRITICAL' | 'WARNING';
}

function useLowStockAlerts(enabled: boolean) {
  return useQuery<LowStockAlert[]>({
    queryKey: ['vendor', 'alerts', 'low-stock'],
    queryFn: () => validatedApi.get('/vendor/alerts/low-stock'),
    staleTime: 1000 * 60 * 5,
    refetchInterval: 1000 * 60 * 5,
    enabled,
  });
}

function formatAlertDate(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00');
  return formatISTLocale(date, { month: 'short', day: 'numeric' });
}

interface LowStockSectionProps {
  alerts: LowStockAlert[];
  isLoading: boolean;
  dismissedIds: Set<string>;
  onDismiss: (key: string) => void;
  onNavigate: () => void;
}

function LowStockSection({
  alerts,
  isLoading,
  dismissedIds,
  onDismiss,
  onNavigate,
}: LowStockSectionProps) {
  const navigate = useNavigate();
  const venueCtx = useContext(VenueContext);
  const venueId = venueCtx?.venueId;

  const visible = alerts.filter(
    (a) => !dismissedIds.has(`${a.addonName}-${a.venueName}-${a.date}`)
  );

  return (
    <Box>
      <Box
        sx={{
          px: 2,
          py: 1,
          bgcolor: 'action.hover',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <StockIcon fontSize="small" color="warning" />
        <Typography variant="caption" fontWeight={600} color="text.secondary">
          LOW STOCK ALERTS
        </Typography>
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={20} />
        </Box>
      ) : visible.length === 0 ? (
        <Box sx={{ px: 2, py: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            All stock levels are healthy
          </Typography>
        </Box>
      ) : (
        <>
          <List disablePadding>
            {visible.map((alert, idx) => {
              const key = `${alert.addonName}-${alert.venueName}-${alert.date}`;
              return (
                <ListItem
                  key={key}
                  disableGutters
                  sx={{
                    px: 2,
                    py: 1.5,
                    borderBottom: idx < visible.length - 1 ? '1px solid' : 'none',
                    borderColor: 'divider',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      width: '100%',
                      mb: 0.5,
                    }}
                  >
                    <Typography variant="body2" fontWeight={600} sx={{ flex: 1, mr: 1 }}>
                      {alert.addonName}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Chip
                        label={alert.severity}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.65rem',
                          fontWeight: 700,
                          bgcolor: alert.severity === 'CRITICAL' ? 'error.main' : 'warning.main',
                          color: 'white',
                        }}
                      />
                      <IconButton
                        size="small"
                        aria-label={`Dismiss alert for ${alert.addonName}`}
                        onClick={() => onDismiss(key)}
                      >
                        <CloseIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Box>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {alert.venueName} · {formatAlertDate(alert.date)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                    {alert.remaining} of {alert.totalCapacity} remaining
                  </Typography>
                </ListItem>
              );
            })}
          </List>
          <Box sx={{ p: 1, textAlign: 'center' }}>
            <Button
              size="small"
              onClick={() => {
                if (venueId) {
                  navigate(`/vendor/${venueId}/addon-analytics`);
                }
                onNavigate();
              }}
            >
              View Analytics
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
}

// ─── Main component ─────────────────────────────────────────────────────────

export function NotificationCenter() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [dismissedStockIds, setDismissedStockIds] = useState<Set<string>>(new Set());
  const open = Boolean(anchorEl);

  const isVendor = user?.role === Role.VENDOR || user?.role === Role.VENDOR_STAFF;

  // General notifications
  const { data: unreadCount = 0 } = useUnreadCount();
  const { data: notificationsData, isLoading } = useNotifications({ limit: 10 });

  // Mutations
  const markAsRead = useMarkAsRead();
  const markAllAsRead = useMarkAllAsRead();
  const deleteNotification = useDeleteNotification();

  // Low-stock alerts (vendor only)
  const { data: stockAlerts = [], isLoading: stockLoading } = useLowStockAlerts(isVendor);
  const visibleStockAlerts = stockAlerts.filter(
    (a) => !dismissedStockIds.has(`${a.addonName}-${a.venueName}-${a.date}`)
  );

  // Combined badge count
  const totalBadgeCount = unreadCount + (isVendor ? visibleStockAlerts.length : 0);

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMarkAsRead = (notificationId: string) => {
    markAsRead.mutate(notificationId);
  };

  const handleMarkAllAsRead = () => {
    markAllAsRead.mutate();
  };

  const handleDelete = (notificationId: string) => {
    deleteNotification.mutate(notificationId);
  };

  const handleNotificationClick = (notification: Notification) => {
    handleClose();
    const route = getNotificationRoute(notification, user?.role);
    if (route) {
      navigate(route);
    }
  };

  const handleViewAll = () => {
    handleClose();
    navigate('/notifications');
  };

  const handleDismissStock = (key: string) => {
    setDismissedStockIds((prev) => new Set([...prev, key]));
  };

  // Deduplicate notifications: keep only the most recent per type+referenceId
  const rawNotifications = notificationsData?.data || [];
  const notifications = rawNotifications.filter((n, index, arr) => {
    const key = `${n.type}::${n.referenceId ?? ''}`;
    return arr.findIndex((other) => `${other.type}::${other.referenceId ?? ''}` === key) === index;
  });
  const hasNotifications = notifications.length > 0;

  return (
    <>
      <IconButton
        onClick={handleOpen}
        sx={{
          color: open ? 'primary.main' : 'inherit',
        }}
        aria-label={`${totalBadgeCount} unread notifications`}
      >
        <Badge
          badgeContent={totalBadgeCount}
          color="error"
          max={99}
          sx={{
            '& .MuiBadge-badge': {
              fontSize: '0.65rem',
              height: 18,
              minWidth: 18,
            },
          }}
        >
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            sx: {
              width: 380,
              maxWidth: '100vw',
              maxHeight: 520,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            },
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.5,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="subtitle1" fontWeight={600}>
            Notifications
            {unreadCount > 0 && (
              <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                ({unreadCount} unread)
              </Typography>
            )}
          </Typography>

          {unreadCount > 0 && (
            <Button
              size="small"
              startIcon={<MarkAllReadIcon />}
              onClick={handleMarkAllAsRead}
              disabled={markAllAsRead.isPending}
            >
              Mark all read
            </Button>
          )}
        </Box>

        {/* Scrollable body */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {/* General notification list */}
          <Box sx={{ p: 1 }}>
            {isLoading ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {[1, 2, 3].map((i) => (
                  <Box key={i} sx={{ display: 'flex', gap: 1.5, p: 1.5 }}>
                    <Skeleton variant="circular" width={40} height={40} />
                    <Box sx={{ flex: 1 }}>
                      <Skeleton variant="text" width="60%" />
                      <Skeleton variant="text" width="100%" />
                      <Skeleton variant="text" width="30%" />
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : hasNotifications ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {notifications.map((notification) => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    onMarkAsRead={handleMarkAsRead}
                    onDelete={handleDelete}
                    onClick={handleNotificationClick}
                  />
                ))}
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: 4,
                  color: 'text.secondary',
                }}
              >
                <EmptyIcon sx={{ fontSize: 40, mb: 1, opacity: 0.5 }} />
                <Typography variant="body2" color="text.secondary">
                  No notifications yet
                </Typography>
                <Typography variant="caption" color="text.disabled">
                  We'll notify you when something happens
                </Typography>
              </Box>
            )}
          </Box>

          {/* Low-stock section (vendor only) */}
          {isVendor && (
            <>
              <Divider />
              <LowStockSection
                alerts={stockAlerts}
                isLoading={stockLoading}
                dismissedIds={dismissedStockIds}
                onDismiss={handleDismissStock}
                onNavigate={handleClose}
              />
            </>
          )}
        </Box>

        {/* Footer */}
        {hasNotifications && (
          <>
            <Divider />
            <Box sx={{ p: 1 }}>
              <Button fullWidth onClick={handleViewAll} sx={{ textTransform: 'none' }}>
                View all notifications
              </Button>
            </Box>
          </>
        )}
      </Popover>
    </>
  );
}
